import { type OnDemandData, VodContentType, VodType, ContentType, isVMedia, type VodPagingRequestData } from "@ca.vmedia.tv/domains";
import { useRoute } from 'vue-router'
import { MostPopularSlideData } from "@/components/MostPopularSlider/types";
import { AppConstants } from "@/common";
import { Services } from "@ca.vmedia.tv/services";
import type { GenresSubitemsRequest } from "@/stores/Genres/types/GenresSubitemsRequest";
import { Logger } from '@/utils';
import { useApplicationStore } from "@/stores/Application";
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { AxiosError } from 'axios';
import type { ExposeInterface } from './types';

export const useMostPopularVod = defineStore('MostPopularVod', (): ExposeInterface => {
    const { 
        emptyOnDemandData
    } = useEmptyLine();

    const ApplicationStore = useApplicationStore();
    const {
        getFilterVodParams
    } = storeToRefs(ApplicationStore);

    const route = useRoute();
    const mostPopularVod: Ref<OnDemandData | null> = ref(null);
    const mostPopularVodDetailGenre: Ref<{
        data: OnDemandData | null;
        genreId: number | null;
    }> = ref({
        data: null,
        genreId: null,
    });

    const indexMostPopular: Ref<number> = ref(0);

    const getIndexMostPopular = computed((): number => indexMostPopular.value);

    const getMostPopularVod = computed((): MostPopularSlideData[] | null => {
        const items = mostPopularVod.value?.items ?? null;
        const filters = route.query;

        if (items === null) {
            return null;
        }

        if (items.length === 0) {
            return [];
        }
        let updatedItems = items;
        if (filters && filters.filter && filters.filter !== 'All' && filters.filter !== "null") {
            updatedItems = items.filter(item => item.showType === filters.filter);
        }

        return updatedItems
            .filter((show) => show.id !== null)
            .map(
                (show) =>
                    new MostPopularSlideData(show)
            );
    });

    const getMostPopularVodDetailGenre = computed((): MostPopularSlideData[] | null => {
        const items = mostPopularVodDetailGenre.value.data?.items ?? null;
        if (items === null) {
            return null;
        }

        if (items.length === 0) {
            return [];
        }

        return items
            .filter((show) => show.id !== null)
            .map(
                (show) =>
                    new MostPopularSlideData(show)
            )
    });

    /**
     * Fetch most popular movies and show
     * @param {VodPagingRequestData} requestData Fetch parameters
     */
    const fetchMostPopularVod = async (requestData: VodPagingRequestData) => {
        try {
            if (getMostPopularVod.value !== null) {
                return;
            }

            const { showType } = getFilterVodParams.value;
            mostPopularVod.value = await Services.Instance.vod.getPopularContent(
                ContentType.SVOD,
                requestData.itemsCount ?? AppConstants.ItemsInLine,
                requestData.pageNumber ?? 1,
                VodType.All,
                VodContentType.MostPopularVod,
                undefined,
                showType
            );
        } catch (error) {
            mostPopularVod.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    /**
     * Fetch most popular movies and show for detail-genre page
     * @param {GenresSubitemsRequest} requestData Fetch parameters
     */
    const fetchMostPopularVodDetailGenre = async (requestData: GenresSubitemsRequest) => {
        try {
            if (getMostPopularVodDetailGenre.value !== null && mostPopularVodDetailGenre.value.genreId === requestData.genre.id) {
                return;
            }

            const vodType = (isVMedia && (requestData.genre.id == 514 || requestData.genre.parentId == 514)) ? VodType.CraveTV : VodType.All;
            const { showType } = getFilterVodParams.value;
            const data = await Services.Instance.vod.getPopularContent(
                ContentType.SVOD,
                requestData.itemsPerPage ?? AppConstants.ItemsInLine,
                requestData.pageNumber ?? 1,
                vodType,
                VodContentType.MostPopularVod,
                requestData.genre,
                showType
            );
            mostPopularVodDetailGenre.value = {data, genreId: requestData.genre.id};
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const setIndexMostPopular = (index: number) => {
        indexMostPopular.value = index;
    };

    const clearIndexMostPopular = () => {
        indexMostPopular.value = 0;
    };

    const clearMostPopularVodDetailGenre = () => {
        mostPopularVodDetailGenre.value.data = null;
        mostPopularVodDetailGenre.value.genreId = null;
    };

    const clearMostPopularVod = () => {
        mostPopularVod.value = null;
    };

    const clearAllMostPopularVod = () => {
        clearIndexMostPopular();
        clearMostPopularVod();
        clearMostPopularVodDetailGenre();
    };

    return {
        getMostPopularVod,
        getMostPopularVodDetailGenre,
        getIndexMostPopular,
        fetchMostPopularVod,
        fetchMostPopularVodDetailGenre,
        setIndexMostPopular,
        clearIndexMostPopular,
        clearMostPopularVodDetailGenre,
        clearMostPopularVod,
        clearAllMostPopularVod
    };
});